import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { flattenQuery } from 'helpers/flattenQuery';
import { FilterConfig } from 'hooks/useListQuery';
import { REJECT, array, nullable, num, object, acceptDegraded, str, unknownValue } from 'validation';
import { filterConfigSchema } from 'api/shared/listConfig';
import { toDraft } from './toDraft';
import { readResponse } from 'api/shared/readResponse';

// Wire types re-derived from nap-backend (routes/audit.js + migrations
// 0003_table_audit.sql and 0042_table_audit_trail_object_type.sql) — NOT
// from the prior hand-written interface, which was populated from `any` and
// drifted from the backend (phantom `action`, `audit_id` vs `audit_trail_id`,
// missed nullability on user_id/username/object_*). Validated wire data is
// immutable, so the types are deeply readonly.
export interface AuditTrailRecord {
  readonly audit_trail_id: number;
  readonly audit_type: string;
  readonly user_id: number | null;
  readonly username: string | null;
  readonly audit_details: unknown;
  // `audit_trail.created_at` is a nullable column (DEFAULT now(), no NOT NULL). Third copy of this shape after
  // `api/shared/auditTrail` and the folder audit trail — all three read the same table.
  readonly created_at: string | null;
  readonly object_type: string | null;
  readonly object_id: number | null;
}

export interface HeaderConfigEntry {
  readonly header_key: string;
  readonly label: string;
  readonly header_type: string;
}

interface ListPagination {
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

interface AuditTrailListResponse {
  readonly data: readonly AuditTrailRecord[];
  readonly pagination: ListPagination;
  readonly config: readonly HeaderConfigEntry[];
  readonly filtersConfig: readonly FilterConfig[];
}

interface AuditTrailCountResponse {
  readonly total_records: number;
  readonly total_pages: number;
  readonly limit: number;
}

const auditTrailRecordSchema = object<AuditTrailRecord>({
  audit_trail_id: num(REJECT),
  audit_type: str(),
  user_id: nullable(num()),
  username: nullable(str()),
  audit_details: unknownValue(),
  created_at: nullable(str()),
  object_type: nullable(str()),
  object_id: nullable(num()),
});

const headerConfigSchema = object<HeaderConfigEntry>({
  header_key: str(),
  label: str(),
  header_type: str(),
});

const listPaginationSchema = object<ListPagination>({
  current_page: num(),
  limit: num(),
  offset: num(),
});

const auditTrailListResponseSchema = object<AuditTrailListResponse>({
  data: array(auditTrailRecordSchema),
  pagination: listPaginationSchema,
  config: array(headerConfigSchema),
  filtersConfig: array(filterConfigSchema),
});

const auditTrailCountResponseSchema = object<AuditTrailCountResponse>({
  total_records: num(REJECT),
  total_pages: num(REJECT),
  limit: num(),
});

// State pagination is the union maintained across the two endpoints: the list
// endpoint supplies current_page/limit/offset, the count endpoint supplies
// total_records/total_pages.
interface PaginationState {
  readonly total_records: number;
  readonly total_pages: number;
  readonly current_page: number;
  readonly limit: number;
  readonly offset: number;
}

interface AuditTrailState {
  readonly records: readonly AuditTrailRecord[];
  readonly pagination: PaginationState;
  readonly status: 'idle' | 'loading' | 'succeeded' | 'failed';
  readonly error: string | null;
  readonly config: readonly HeaderConfigEntry[];
  readonly filtersConfig: readonly FilterConfig[];
  readonly loading: {
    readonly fetchAuditTrails: boolean;
    readonly fetchAuditTrailCount: boolean;
  };
}

const initialState: AuditTrailState = {
  records: [],
  pagination: {
    total_records: 0,
    total_pages: 0,
    current_page: 1,
    limit: 10,
    offset: 0,
  },
  status: 'idle',
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    fetchAuditTrails: false,
    fetchAuditTrailCount: false,
  },
};

export const fetchAuditTrails = createAsyncThunk('auditTrail/fetchAuditTrails', async (params: Record<string, any>) => {
  const res = await api.get('/audit_trail', { params: flattenQuery(params) });
  return acceptDegraded(readResponse(res.data, { schema: auditTrailListResponseSchema, source: 'fetchAuditTrails' }));
});

export const fetchAuditTrailCount = createAsyncThunk('auditTrail/fetchAuditTrailCount', async (params: Record<string, any>, { signal }) => {
  const res = await api.get('/audit_trail/count', {
    params: flattenQuery(params),
    signal,
    timeout: 180000, // 3 minutes timeout
  });
  return acceptDegraded(readResponse(res.data, { schema: auditTrailCountResponseSchema, source: 'fetchAuditTrailCount' }));
});

const auditTrailSlice = createSlice({
  name: 'auditTrail',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditTrails.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.fetchAuditTrails = true;
      })
      .addCase(fetchAuditTrails.fulfilled, (state, action) => {
        const { data, pagination, config, filtersConfig } = action.payload;
        state.records = toDraft(data);
        state.config = toDraft(config);
        state.filtersConfig = toDraft(filtersConfig);
        // The list endpoint carries current_page/limit/offset only; preserve
        // total_records/total_pages, which arrive from the count endpoint.
        state.pagination = { ...state.pagination, ...pagination };
        state.status = 'succeeded';
        state.loading.fetchAuditTrails = false;
      })
      .addCase(fetchAuditTrails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch audit trails';
        state.loading.fetchAuditTrails = false;
      })
      .addCase(fetchAuditTrailCount.pending, (state) => {
        state.loading.fetchAuditTrailCount = true;
      })
      .addCase(fetchAuditTrailCount.fulfilled, (state, action) => {
        const { total_records, total_pages, limit } = action.payload;
        state.pagination.total_records = total_records;
        state.pagination.total_pages = total_pages;
        if (limit) {
          state.pagination.limit = limit;
        }
        state.loading.fetchAuditTrailCount = false;
      })
      .addCase(fetchAuditTrailCount.rejected, (state, action) => {
        // Don't set error or update loading state if it was aborted
        // The aborted request should not affect the loading state if a new request is pending
        if (action.error.name === 'AbortError' || action.error.name === 'CanceledError') {
          // If aborted, only set loading to false if no new request is pending
          // This prevents the loading state from flickering when requests are cancelled
          return;
        }
        // Only set loading to false for actual errors (not aborts)
        state.error = action.error.message || 'Failed to fetch audit trail count';
        state.loading.fetchAuditTrailCount = false;
      });
  },
});

export default auditTrailSlice.reducer;
